import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchUserAttributes } from "aws-amplify/auth";
import { toast } from "react-toastify";
import {
  PlatformSupportTicketAPI,
  SupportTicket,
  TicketAttachment,
  TicketMessage,
  TicketStatus,
} from "../../services/supportTicket.service";
import { ChatIcon, ChatPanel, CountBadge } from "../../components/support/ChatPanel";
import { SupportBanner } from "../../components/support/SupportBanner";
import { refreshSupportUnread, useSupportUnread } from "../../hooks/useSupportUnread";
import PlatformAdminNav from "../../components/platform/PlatformAdminNav";
import PlatformAdminHeader from "../../components/platform/PlatformAdminHeader";

// "unresolved" = open + in progress (the default inbox view).
type StatusFilter = TicketStatus | "all" | "unresolved";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "unresolved",  label: "Status: Unresolved" },
  { value: "all",         label: "Status: All" },
  { value: "open",        label: "Status: Open" },
  { value: "in_progress", label: "Status: In progress" },
  { value: "resolved",    label: "Status: Resolved" },
  { value: "closed",      label: "Status: Closed" },
];

const STATUS_STYLES: Record<string, string> = {
  open:        "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  resolved:    "bg-green-50 text-green-700 border-green-200",
  closed:      "bg-gray-100 text-gray-600 border-gray-200",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const DetailRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) =>
  value ? (
    <div className="flex gap-2">
      <dt className="w-20 flex-shrink-0 text-slate-400">{label}</dt>
      <dd className="break-words">{value}</dd>
    </div>
  ) : null;

const SupportTicketsPage: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("unresolved");
  const [selected, setSelected] = useState<SupportTicket | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const { summary, markAllRead } = useSupportUnread("admin");
  const [chatTicketId, setChatTicketId] = useState<string | null>(null);
  const threadEndRef = useRef<HTMLDivElement>(null);
  // Which photo/screen-recording is shown full-size inline, within the SAME ticket
  // modal — never a new tab, and "back" only closes the viewer, not the ticket.
  const [viewingAttachment, setViewingAttachment] = useState<TicketAttachment | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const enterFullscreen = () => {
    previewContainerRef.current?.requestFullscreen?.();
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
  };

  const load = () => {
    setLoading(true);
    // The admin-email badge is cosmetic — a hiccup fetching it must never
    // block the actual ticket list from showing.
    fetchUserAttributes()
      .then(attrs => setAdminEmail(attrs.email ?? ""))
      .catch(() => { /* non-critical — badge just stays empty */ });
    PlatformSupportTicketAPI.list()
      .then(setTickets)
      .catch(() => setError("Failed to load support tickets. Please refresh."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Pick up new tickets and clinic replies without a manual refresh; a reply that
  // lands while its ticket is open is read straight away.
  useEffect(() => {
    const id = window.setInterval(() => {
      PlatformSupportTicketAPI.list()
        .then(fresh => {
          setTickets(fresh);
          const chatting = fresh.find(t => t.ticket_id === chatTicketId);
          if (chatting && chatting.unread_count > 0) {
            PlatformSupportTicketAPI.markRead(chatting.ticket_id).then(refreshSupportUnread).catch(() => {});
          }
          setSelected(cur => {
            if (!cur) return cur;
            const upd = fresh.find(t => t.ticket_id === cur.ticket_id);
            if (!upd || upd.messages.length === cur.messages.length) return cur;
            PlatformSupportTicketAPI.markRead(cur.ticket_id).then(refreshSupportUnread).catch(() => {});
            return { ...cur, messages: upd.messages, status: upd.status, unread_count: 0 };
          });
        })
        .catch(() => { /* silent */ });
    }, 20_000);
    return () => window.clearInterval(id);
  }, [chatTicketId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tickets.filter(t => {
      if (statusFilter === "unresolved") {
        if (t.status !== "open" && t.status !== "in_progress") return false;
      } else if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (q) {
        return (
          t.subject.toLowerCase().includes(q) ||
          t.reporter_name.toLowerCase().includes(q) ||
          (t.clinic_name ?? "").toLowerCase().includes(q) ||
          t.reporter_email.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tickets, search, statusFilter]);

  const chatTicket = tickets.find(t => t.ticket_id === chatTicketId) ?? null;
  const openCount = tickets.filter(t => t.status === "open").length;

  useEffect(() => {
    threadEndRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selected?.ticket_id, selected?.messages.length]);

  const openTicket = (t: SupportTicket) => {
    setSelected(t);
    setNotesDraft(t.admin_notes ?? "");
    setViewingAttachment(null);
    exitFullscreen();
    PlatformSupportTicketAPI.get(t.ticket_id)
      .then(full => setSelected(cur => (cur && cur.ticket_id === full.ticket_id ? { ...full, unread_count: 0 } : cur)))
      .catch(() => { /* list data is still shown */ });
    setTickets(prev => prev.map(x => (x.ticket_id === t.ticket_id ? { ...x, unread_count: 0 } : x)));
    PlatformSupportTicketAPI.markRead(t.ticket_id).then(refreshSupportUnread).catch(() => { /* next poll corrects it */ });
  };

  const closeViewer = () => {
    exitFullscreen();
    setViewingAttachment(null);
  };

  const handleStatusChange = async (status: TicketStatus) => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await PlatformSupportTicketAPI.update(selected.ticket_id, status, notesDraft);
      setSelected(updated);
      setTickets(prev => prev.map(t => (t.ticket_id === updated.ticket_id ? updated : t)));
      toast.success("Ticket updated.");
    } catch (err: any) {
      toast.error(err?.message || "Could not update the ticket.");
    } finally {
      setSaving(false);
    }
  };

  const handleReplySent = (ticketId: string, msg: TicketMessage) => {
    // Mirror the server: first admin reply moves an open ticket to in progress.
    const apply = (t: SupportTicket): SupportTicket => ({
      ...t,
      status: t.status === "open" ? "in_progress" : t.status,
      messages: [...t.messages, msg],
    });
    setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? apply(t) : t)));
    setSelected(cur => (cur && cur.ticket_id === ticketId ? apply(cur) : cur));
  };

  const handleUnsend = async (ticketId: string, messageId: string) => {
    try {
      await PlatformSupportTicketAPI.unsend(ticketId, messageId);
      const wipe = (t: SupportTicket): SupportTicket => ({
        ...t,
        messages: t.messages.map(m => (m.message_id === messageId ? { ...m, is_deleted: true, body: "", attachments: [] } : m)),
      });
      setTickets(prev => prev.map(t => (t.ticket_id === ticketId ? wipe(t) : t)));
      setSelected(cur => (cur && cur.ticket_id === ticketId ? wipe(cur) : cur));
    } catch (err: any) {
      toast.error(err?.message || "Could not unsend the message.");
    }
  };

  const handleDeleteTicket = async (t: SupportTicket) => {
    if (!window.confirm(`Delete "${t.subject}" from ${t.clinic_name ?? t.reporter_name}? This removes the ticket, its attachments and the whole conversation for both sides, and cannot be undone.`)) return;
    try {
      await PlatformSupportTicketAPI.remove(t.ticket_id);
      setTickets(prev => prev.filter(x => x.ticket_id !== t.ticket_id));
      if (chatTicketId === t.ticket_id) setChatTicketId(null);
      setSelected(null);
      refreshSupportUnread();
      toast.success("Ticket deleted.");
    } catch (err: any) {
      toast.error(err?.message || "Could not delete the ticket.");
    }
  };

  const openChat = (ticketId: string) => {
    setChatTicketId(ticketId);
    setTickets(prev => prev.map(x => (x.ticket_id === ticketId ? { ...x, unread_count: 0 } : x)));
    PlatformSupportTicketAPI.markRead(ticketId).then(refreshSupportUnread).catch(() => { /* next poll corrects it */ });
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await PlatformSupportTicketAPI.update(selected.ticket_id, selected.status, notesDraft);
      setSelected(updated);
      setTickets(prev => prev.map(t => (t.ticket_id === updated.ticket_id ? updated : t)));
      toast.success("Notes saved.");
    } catch (err: any) {
      toast.error(err?.message || "Could not save notes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <PlatformAdminNav adminEmail={adminEmail} />

      <div className="max-w-7xl mx-auto px-8 py-8">
        <PlatformAdminHeader subtitle="PlatformAdmin only — in-app support tickets raised by clinic staff." />

        {loading && <div className="text-sm text-slate-500 py-12 text-center">Loading tickets…</div>}

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && (
          <>
            <SupportBanner
              summary={summary}
              from="clinics"
              onOpen={openChat}
              onDismiss={markAllRead}
            />
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-sm font-semibold ${
                openCount === 0 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"
              }`}>
                {openCount} open
              </span>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-[#0066ff]"
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <input
              type="text"
              placeholder="Search subject, clinic or reporter…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#0066ff] w-80"
            />

            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 px-10 py-10 text-center text-sm text-slate-500">
                No support tickets match these filters.
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-md border-2 border-slate-300 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                    <tr>
                      <th className="text-left px-5 py-3">Subject</th>
                      <th className="text-left px-5 py-3">Clinic</th>
                      <th className="text-left px-5 py-3">Raised</th>
                      <th className="text-left px-5 py-3">Attachments</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(t => (
                      <tr
                        key={t.ticket_id}
                        onClick={() => openTicket(t)}
                        className="cursor-pointer hover:bg-slate-50"
                      >
                        <td className="px-5 py-3 max-w-xs text-slate-800">
                          <span className="block truncate">{t.subject}</span>
                        </td>
                        <td className="px-5 py-3 text-slate-600">
                          <div>{t.clinic_name ?? "—"}</div>
                          <div className="text-xs text-slate-400">{t.reporter_name}</div>
                        </td>
                        <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{formatDate(t.created_at)}</td>
                        <td className="px-5 py-3 text-slate-500">{t.attachments.length || "—"}</td>
                        <td className="px-5 py-3">
                          <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[t.status] ?? ""}`}>
                            {t.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); if (chatTicketId === t.ticket_id) setChatTicketId(null); else openChat(t.ticket_id); }}
                            title="Chat with this clinic"
                            aria-label="Chat with this clinic"
                            className={`relative inline-flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                              chatTicketId === t.ticket_id ? "border-sky-700 bg-sky-700 text-white" : "border-sky-300 bg-sky-100 text-sky-700 hover:bg-sky-200"
                            }`}
                          >
                            <ChatIcon className="h-4 w-4" />
                            <CountBadge count={t.unread_count} className="absolute -right-1.5 -top-1.5" />
                          </button>
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); void handleDeleteTicket(t); }}
                            title="Delete ticket"
                            aria-label="Delete ticket"
                            className="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-full border-2 border-red-200 bg-white text-red-600 hover:bg-red-50"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {chatTicket && (
        <ChatPanel
          floating
          title={chatTicket.clinic_name ?? chatTicket.reporter_name}
          subtitle={`${chatTicket.reporter_name} · ${chatTicket.subject}`}
          ownRole="admin"
          otherLabel={chatTicket.reporter_name}
          messages={chatTicket.messages}
          placeholder="Reply to the clinic…"
          requestUploadUrls={PlatformSupportTicketAPI.requestUploadUrls}
          onSend={(body, atts) => PlatformSupportTicketAPI.reply(chatTicket.ticket_id, body, atts)}
          onSent={msg => handleReplySent(chatTicket.ticket_id, msg)}
          onUnsend={id => handleUnsend(chatTicket.ticket_id, id)}
          onClose={() => setChatTicketId(null)}
        />
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => { exitFullscreen(); setSelected(null); }}>
          <div
            className="bg-white rounded-2xl border-2 border-slate-300 shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{selected.subject}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {selected.clinic_name ? `${selected.clinic_name} · ` : ""}{selected.reporter_name} · {formatDate(selected.created_at)}
                </p>
              </div>
              <button onClick={() => { exitFullscreen(); setSelected(null); }} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>


            <div className="mb-3 grid grid-cols-3 gap-2 text-[11px] leading-snug">
              <div className="min-w-0 rounded-lg border-2 border-slate-200 bg-slate-50 p-2.5">
                <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Clinic</p>
                {selected.clinic ? (
                  <dl className="space-y-0.5 text-slate-700">
                    <DetailRow label="Name" value={selected.clinic.name} />
                    <DetailRow label="Location" value={selected.clinic.location} />
                    <DetailRow label="Address" value={selected.clinic.address} />
                    <DetailRow label="Phone" value={selected.clinic.phone} />
                    <DetailRow label="Email" value={selected.clinic.email} />
                    <DetailRow label="Website" value={selected.clinic.website} />
                    <DetailRow label="License" value={selected.clinic.license_number} />
                    <DetailRow label="Size" value={selected.clinic.company_size} />
                  </dl>
                ) : (
                  <p className="text-slate-400">{selected.clinic_name ?? "Details unavailable."}</p>
                )}
              </div>
              <div className="min-w-0 rounded-lg border-2 border-slate-200 bg-slate-50 p-2.5">
                <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Raised by</p>
                <dl className="space-y-0.5 text-slate-700">
                  <DetailRow label="Name" value={selected.reporter?.name ?? selected.reporter_name} />
                  <DetailRow label="Email" value={selected.reporter?.email ?? selected.reporter_email} />
                  <DetailRow label="Phone" value={selected.reporter?.phone} />
                  <DetailRow label="Role" value={selected.reporter?.role} />
                  <DetailRow label="Desig." value={selected.reporter?.designation} />
                  <DetailRow label="Reg. no." value={selected.reporter?.registration_number} />
                </dl>
              </div>
              <div className="min-w-0 rounded-lg border-2 border-slate-200 bg-slate-50 p-2.5">
                <p className="mb-1 font-semibold uppercase tracking-wide text-slate-500">Reported issue</p>
                <p className="max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-slate-700">{selected.description}</p>
                <p className="mt-1 text-slate-400">{formatDate(selected.created_at)}</p>
              </div>
            </div>

            {viewingAttachment ? (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 uppercase truncate">
                    {viewingAttachment.file_name}{" "}
                    <span className="normal-case text-slate-400">
                      — {viewingAttachment.attachment_type === "photo" ? "click image to expand full screen" : "use the player's full-screen button"}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={closeViewer}
                    className="flex-shrink-0 ml-2 inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    ✕ Back to ticket
                  </button>
                </div>
                <div
                  ref={previewContainerRef}
                  className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center [&:fullscreen]:rounded-none"
                >
                  {viewingAttachment.attachment_type === "photo" ? (
                    <img
                      src={viewingAttachment.view_url ?? undefined}
                      alt={viewingAttachment.file_name}
                      onClick={enterFullscreen}
                      className={`cursor-zoom-in object-contain ${isFullscreen ? "max-h-screen max-w-screen" : "w-full max-h-96"}`}
                    />
                  ) : (
                    // No onClick here: a <video>'s native controls (play/pause/seek)
                    // live inside the same element, so a click-anywhere handler would
                    // fire on every control click too. Its control bar already has its
                    // own fullscreen button; our ✕ overlay below still covers exiting
                    // it however it was entered.
                    <video
                      controls
                      autoPlay
                      src={viewingAttachment.view_url ?? undefined}
                      className={`object-contain ${isFullscreen ? "max-h-screen max-w-screen" : "w-full max-h-96"}`}
                    />
                  )}
                  {isFullscreen && (
                    <button
                      type="button"
                      onClick={exitFullscreen}
                      className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/60 text-white text-lg hover:bg-black/80"
                      aria-label="Exit full screen"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ) : selected.attachments.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-xs font-medium text-slate-500 uppercase">Attachments</p>
                {selected.attachments.map(a => (
                  <div key={a.attachment_id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="truncate text-slate-700">{a.file_name} <span className="text-slate-400">({a.attachment_type.replace("_", " ")})</span></span>
                      {a.view_url && a.attachment_type !== "voice_note" && (
                        <button
                          type="button"
                          onClick={() => setViewingAttachment(a)}
                          className="text-blue-600 font-medium ml-2 flex-shrink-0 hover:text-blue-800"
                        >
                          View
                        </button>
                      )}
                    </div>
                    {/* Voice notes play right here — never a separate tab/view. */}
                    {a.attachment_type === "voice_note" && a.view_url && (
                      <audio controls preload="metadata" src={a.view_url} className="h-8 w-full mt-1.5" />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Status</label>
              <select
                value={selected.status}
                disabled={saving}
                onChange={e => handleStatusChange(e.target.value as TicketStatus)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0066ff]"
              >
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Internal notes</label>
              <textarea
                value={notesDraft}
                onChange={e => setNotesDraft(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0066ff]"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleSaveNotes}
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Save notes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsPage;
